import {
  Database,
  LayoutDashboard,
  Leaf,
  LineChart,
  Pin,
} from "lucide-react";
import { lazy } from "react";

import { MarketCommodityToggle } from "@/components/layout/MarketCommodityToggle";
import { MyViewsHeaderButton } from "@/components/market-intelligence/views/MyViewsHeaderButton";
import { MyViewsPanel } from "@/components/market-intelligence/views/MyViewsPanel";
import { POINT_KINDS } from "@/components/market-intelligence/PointKind";
import {
  MI_GAS_PATH,
  MI_GAS_REGIONS_GROUP_PATH,
  MI_PATH,
  MI_REGIONS_GROUP_PATH,
  isoSlug,
  pointPath,
  regionPath,
  regionSlug,
} from "@/lib/market-intelligence/paths";
import { POINT_GROUPS } from "@/lib/market-intelligence/point-groups";
import {
  gasRegionHasSurface,
  isGasScoped,
  pinnedCodes,
  regionHasSurface,
  regionPointsOf,
  regionRoutesSurface,
  useScopedGasRegion,
  useScopedRegion,
} from "@/lib/market-intelligence/region-nav";
import { MARKET_ISOS } from "@/lib/market-intelligence/topology";
import {
  HUB_TAB_ROUTES,
  hubTabsFor,
} from "@/pages/market-intelligence/hubs/hub-tabs";
import {
  GAS_REGION_OVERVIEW,
  GAS_REGION_SURFACES,
  REGION_OVERVIEW,
  REGION_SURFACES,
} from "@/pages/market-intelligence/regions/region-surfaces";

import type { ModuleDefinition } from "@/lib/module-registry";
import type { AppRoute } from "@/lib/routes";
import type { MarketHub } from "@/types/market-intelligence";

/**
 * Market Intelligence.
 *
 * Wholesale market data organised as a drill-down: the Overview shows the
 * country, a region shows everything true of that region, a hub shows its
 * curves.
 *
 * **The region is a SCOPE, not a destination.** Like Energy Advisor's client,
 * a region is picked in the sidebar header and its surfaces are listed
 * beneath — because "RECs" and "Capacity" mean nothing until you say which
 * market, and seven ISO rows each hiding three tabs buries them. Scoping also
 * keeps the sidebar correct while you drill into a hub, where the URL no
 * longer contains the region.
 *
 * **Gas drills down exactly as electric does** (2026-10-02). Its `mi-gas`
 * section is scoped by a gas REGION — Platts' own (Appalachia,
 * Louisiana/Southeast, Northeast, Southwest, Upper Midwest, …) — the way `mi-region` is scoped by an ISO, and lists the
 * region's Hubs and Delivery points beneath — gas's counterparts to power's
 * hubs and load zones (`POINT_GROUPS`). A gas region has no market operator
 * the way an ISO does, but it is how the desk groups gas points, and it gives
 * gas the same Overview → region → point drill-down. RECs and PJM capacity
 * are power-only concepts and don't appear for gas at all.
 *
 * Gas regions are the FEED's (`topology.gasRegions`), not a constant, so their
 * routes are parameterised (`/gas/regions/:region`) where power's are built
 * per ISO.
 *
 * **Every point list comes from the server**, through `recordScope.expandRows`
 * in both sections. The static constants in `topology.ts` are a first-paint
 * fallback and the seeded generator's data, never the resting state.
 *
 * **Short codes everywhere.** CAISO, not California ISO; SP15, not South Path
 * 15. The full names are what a regulator calls them; the codes are what the
 * desk says, and they fit a sidebar row without wrapping.
 *
 * Standalone pages are reserved for ADMIN surfaces, reached through the module
 * settings gear: reference data somebody maintains rather than a market view
 * somebody reads.
 */
const MarketOverviewPage = lazy(() =>
  import("@/pages/market-intelligence/MarketOverviewPage").then((m) => ({
    default: m.MarketOverviewPage,
  })),
);
const HubWorkspacePage = lazy(() =>
  import("@/pages/market-intelligence/HubWorkspacePage").then((m) => ({
    default: m.HubWorkspacePage,
  })),
);
/**
 * The Hubs and Load zones rows' children: that kind of the region's points
 * (`regionPointsOf`), each carrying its own forwards and index rows.
 *
 * The sidebar then reads the same hierarchy the URL does, all the way to
 * `/electric/regions/caiso/hubs/caiso_sp15/forwards` — which is the whole point of
 * nesting hubs under regions. Capacity is left off the hub rows even for PJM:
 * it is a zone-level table the hub page still serves, but a third row on
 * every one of six PJM hubs would push the region's own surfaces off-screen.
 *
 * Every point is a row of its own, under its kind (2026-10-01). Load zones were
 * briefly reached only through their hub, as tabs on its page, and the power
 * desk found that hid them: "very counterintuitive to go into a hub to find
 * load zone pricing."
 *
 * Returns `undefined` for RECs and Capacity, which leaves those plain links.
 */
function expandRegionRow(recordId: string, segment: string): AppRoute[] | undefined {
  if (segment === "hubs") return regionPointsOf(recordId, "hub").map((h) => hubRow(recordId, h));
  if (segment === "load-zones") {
    return regionPointsOf(recordId, "load_zone").map((h) => hubRow(recordId, h));
  }
  return undefined;
}

/**
 * A gas region's Hubs and Delivery points rows' children — `expandRegionRow`
 * for gas. Every row a gas region has is a point group, so none stays a plain
 * link.
 */
function expandGasRegionRow(recordId: string, segment: string): AppRoute[] | undefined {
  const group = POINT_GROUPS.gas.find((g) => g.tab === segment);
  if (!group) return undefined;
  return regionPointsOf(recordId, group.kind, "gas").map((h) => hubRow(recordId, h));
}

/**
 * One point's nav row, with the tabs it can answer.
 *
 * Leads with its kind's icon (`PointKind.tsx`), which is how the flat list
 * says hub or load zone: the same icon its card and its page header carry.
 *
 * The sidebar stops at the tab (Forwards, Index). The views inside a tab
 * (Strips/Curve, Trends/Hourly/Basis) are chosen on the page, in the tab's own
 * card, so a tab row stays filled on any view beneath it (`alsoActiveOn` its
 * own path).
 */
function hubRow(recordId: string, hub: MarketHub): AppRoute {
  // `recordId` is the region in scope, an ISO or a gas region: the point's
  // own `iso`, passed explicitly because the sidebar already holds it.
  const at = { commodity: hub.commodity, iso: recordId, code: hub.code };
  return {
    path: pointPath(at),
    label: hub.name,
    icon: POINT_KINDS[hub.kind].icon,
    // A pinned point carries the same marker the cards use, so the sidebar and
    // the grid say the same thing about it. TRAILING rather than leading: the
    // icon slot leads a row and reads as "what kind of thing is this", which a
    // pin is not -- after the label it reads as a note about a row the label
    // has already identified.
    trailingIcon: pinnedCodes(hub.commodity).has(hub.code) ? Pin : undefined,
    status: "live" as const,
    element: <></>,
    // Only the tabs this hub can answer — the sidebar and the tab strip read
    // the same rule, so a row can never lead to a tab the page has dropped.
    subItems: hubTabsFor(hub).map((tab) => {
      const tabPath = pointPath(at, tab.path as "forwards" | "index");
      return {
        path: tabPath,
        label: tab.label,
        icon: tab.icon,
        status: "live" as const,
        element: <></>,
        alsoActiveOn: [tabPath],
      };
    }),
  };
}

/**
 * A gas region's scoped rows — `REGION_SCOPED_ROWS` for gas, from the same
 * kind of registry. Both open on the region's bare url, which shows both
 * groups.
 */
const GAS_REGION_SCOPED_ROWS: AppRoute[] = GAS_REGION_SURFACES.map((surface) => ({
  path: surface.path,
  label: surface.label,
  icon: surface.icon,
  openOn: [""],
  showCount: true,
  status: "live" as const,
  element: <></>,
}));

/**
 * The gas region pages and the gas point workspace.
 *
 * Parameterised on `:region`, where power registers a route per ISO: gas
 * regions come from the feed (`topology.gasRegions`), and routes are built at
 * module load, before any fetch. The pages resolve the param against the
 * topology themselves (`useRegionScope`), so a region the feed adds is
 * reachable the day it appears.
 *
 * `…/:region/hubs` (the Hubs surface) and `…/:region/hubs/:hub` (a point) are
 * different depths, as they are for power, so they cannot shadow each other.
 */
const gasRoutes: AppRoute[] = [
  {
    path: `${MI_GAS_REGIONS_GROUP_PATH}/:region`,
    label: "Gas region",
    status: "live",
    element: GAS_REGION_OVERVIEW.render(),
  },
  ...GAS_REGION_SURFACES.map((surface) => ({
    path: `${MI_GAS_REGIONS_GROUP_PATH}/:region/${surface.path}`,
    label: `Gas ${surface.label.toLowerCase()}`,
    status: "live" as const,
    element: surface.render(),
  })),
  {
    path: `${MI_GAS_REGIONS_GROUP_PATH}/:region/hubs/:hub`,
    label: "Gas point",
    status: "live",
    element: <HubWorkspacePage />,
    children: [...HUB_TAB_ROUTES],
  },
];

/**
 * The scoped region's surfaces, as relative segments.
 *
 * Derived from the same registry that builds the routes, so a new surface
 * appears in the sidebar automatically and cannot drift from what exists. The
 * element is a placeholder: `sectionRoutes` returns `[]` for a `recordScope`
 * section, so these never reach the router — the real routes are built below.
 */
const REGION_SCOPED_ROWS: AppRoute[] = REGION_SURFACES.map((surface) => ({
  path: surface.path,
  label: surface.label,
  icon: surface.icon,
  // The region's All view (its bare url) shows hubs and load zones together,
  // so it opens both groups.
  openOn: surface.path === "hubs" || surface.path === "load-zones" ? [""] : undefined,
  // RECs and Capacity have no children, so they are plain links and print
  // nothing; the flag is only read on a row with children.
  showCount: surface.path === "hubs" || surface.path === "load-zones",
  status: "live" as const,
  element: <></>,
}));

/**
 * One route per ISO per surface: `/regions/pjm/hubs`, `/regions/pjm/recs`, …
 *
 * Flat routes rather than a region page with tab children. The sidebar
 * already lists these surfaces under the region in scope, so a tab strip
 * repeating them gave the reader two controls for one choice — and two places
 * to look for where they were.
 *
 * Registered as hidden routes rather than nav entries: the region picker is
 * how the sidebar reaches them, so listing every ISO as well would say the same
 * thing twice.
 *
 * A bare `/regions/pjm` is the region overview, unfiltered: every pricing point,
 * hubs and load zones together. The region picker, the breadcrumb and the
 * Overview's region cards all land there; the Hubs and Load zones rows are the
 * same page filtered to one kind.
 *
 * **This list is the static `MARKET_ISOS`, while the picker now reads the
 * server's topology, and those two sets can differ.** Routes are built at
 * module-definition time — before any fetch — so they cannot be data-driven
 * without restructuring the registry. The difference is benign in the direction
 * it actually occurs: a region the feed has dropped (SPP today) keeps a
 * registered route, so an old bookmark renders the region page's own empty
 * state instead of a hard 404, and nothing in the UI links there any more. The
 * reverse — a NEW ISO appearing in the feed — would have no route until it is
 * added here, which is the line to change when that happens.
 */
const regionRoutes: AppRoute[] = [...MARKET_ISOS].flatMap((iso) => [
  ...REGION_SURFACES.filter((s) => regionRoutesSurface(iso.code, s.path)).map(
    (surface) => ({
      path: regionPath(iso.code, surface.path),
      label: `${iso.code} ${surface.label}`,
      status: "live" as const,
      element: surface.render(iso.code),
    }),
  ),
  {
    path: regionPath(iso.code),
    label: iso.code,
    status: "live" as const,
    element: REGION_OVERVIEW.render(iso.code),
  },
]);

/**
 * The hub workspace, nested under its region.
 *
 * A separate route rather than a child of the region page: a hub replaces the
 * region view rather than rendering inside it, so it needs its own element at
 * the top level, and its own tab children.
 */
const hubRoutes: AppRoute[] = [...MARKET_ISOS].map((iso) => ({
  path: `${regionPath(iso.code)}/hubs/:hub`,
  label: "Hub",
  status: "live",
  element: <HubWorkspacePage />,
  children: [...HUB_TAB_ROUTES],
}));

export const MARKET_INTELLIGENCE_MODULE: ModuleDefinition = {
  key: "market-intelligence",
  label: "Market Intelligence",
  icon: LineChart,
  // `description` omitted — inherited from the launcher tile.
  entryPath: MI_PATH,
  requiredPermission: "market_intelligence.access",
  sidebarHeader: () => <MarketCommodityToggle />,
  // A bookmark in the top bar opens "My views" at the right of the page — every
  // saved view, so none needs hunting down from its point.
  topbarAction: MyViewsHeaderButton,
  sidePanel: MyViewsPanel,
  sections: [
    {
      // Each commodity has its own Overview row, at its own url, and only the
      // one in scope shows. A single row at the module root sent a gas reader
      // back to the ELECTRIC Overview — the gas one was the same url plus
      // `?commodity=gas`, which a nav link cannot carry.
      key: "mi-explore",
      label: "Explore",
      visibleWhen: (loc) => !isGasScoped(loc),
      routes: [
        {
          path: MI_PATH,
          label: "Overview",
          icon: LayoutDashboard,
          status: "live",
          element: <MarketOverviewPage />,
          title: "Market Intelligence",
        },
      ],
    },
    {
      key: "mi-gas-explore",
      label: "Explore",
      visibleWhen: isGasScoped,
      routes: [
        {
          path: MI_GAS_PATH,
          label: "Overview",
          icon: LayoutDashboard,
          status: "live",
          element: <MarketOverviewPage />,
          title: "Market Intelligence",
        },
      ],
    },
    {
      // The region in scope, with its surfaces beneath — the same shape
      // Energy Advisor uses for a client. Written order, not A–Z: Hubs is the
      // way in, and RECs and Capacity are what else the market carries.
      //
      // Hidden while the sidebar toggle (or the Overview's own `?commodity=`)
      // has Gas selected — `mi-gas` takes its place. See
      // `useMarketCommodityScope`, which both this and `MarketCommodityToggle`
      // read so the two can never disagree about which is showing.
      key: "mi-region",
      preserveOrder: true,
      visibleWhen: (loc) => !isGasScoped(loc),
      recordScope: {
        basePath: MI_REGIONS_GROUP_PATH,
        useScope: useScopedRegion,
        pickerPlaceholder: "Select a region",
        picker: "region",
        // MISO and SPP run no certificate program, and only PJM clears a
        // capacity auction — the same rule the region page applies to its tabs.
        filterRows: regionHasSurface,
        // Hubs expands into the region's own hubs, each with its surfaces.
        expandRows: expandRegionRow,
        // The scope is keyed by short code; the url is lowercased.
        toSlug: isoSlug,
      },
      routes: REGION_SCOPED_ROWS,
    },
    {
      // Gas's counterpart to `mi-region`, and built the same way: a region
      // picked in the sidebar header, its Hubs and Delivery points beneath,
      // each expanding into its points. Visible exactly when `mi-region` is
      // not — the two are mutually exclusive views of the same sidebar area,
      // chosen by `MarketCommodityToggle`.
      key: "mi-gas",
      preserveOrder: true,
      visibleWhen: isGasScoped,
      recordScope: {
        basePath: MI_GAS_REGIONS_GROUP_PATH,
        useScope: useScopedGasRegion,
        pickerPlaceholder: "Select a region",
        picker: "gas-region",
        // The Northeast has no supply hub, as NYISO has no power hub: a row
        // for a kind the region has none of is dropped.
        filterRows: gasRegionHasSurface,
        expandRows: expandGasRegionRow,
        // Keyed by the region's code ("Louisiana/Southeast"); the url is its slug.
        toSlug: regionSlug,
      },
      routes: GAS_REGION_SCOPED_ROWS,
    },
    {
      // The `-settings` suffix is what moves a section behind the sidebar
      // gear; see SETTINGS_SECTION_SUFFIX in the module registry.
      key: "mi-settings",
      label: "Settings",
      routes: [
        {
          // The editor lives in Admin, and there is deliberately only one.
          // This module used to carry a read-only copy of the same table,
          // which is how its numbers drifted from the reference data in the
          // first place; the row now points at the console that owns them.
          path: "/settings/pjm-capacity",
          label: "Capacity Market Data",
          icon: Database,
          status: "live",
          crossLink: true,
          requiresSuperAdmin: true,
        },
        {
          // Same arrangement as the row above: the editor lives in Admin and
          // this points at it. What a certificate is CALLED is the only part
          // of the REC feed anyone maintains by hand — the prices mirror from
          // the lake — so this is the one place that needs an editor at all.
          path: "/settings/rec-certificates",
          label: "REC Certificates",
          icon: Leaf,
          status: "live",
          crossLink: true,
          requiresSuperAdmin: true,
        },
      ],
    },
  ],
  hiddenRoutes: [...regionRoutes, ...hubRoutes, ...gasRoutes],
};
